/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useEditorMainContext } from "../contexts/EditorMainContext";
import { useCallback, useState } from "react";
import { useSimpleEvent } from "./useSimpleEvent";

export function useEditorState<T>(
	storageKey: string,
	defaultValue: T
): [state: T, setState: (state: T) => void] {
	const { stateService } = useEditorMainContext();
	const [ localState, setLocalState ] = useState<T>(() => {
		if (!stateService.has(storageKey)) {
			stateService.register(storageKey, defaultValue);
		}

		return stateService.get(storageKey);
	});

	const setState = useCallback<(state: T) => void>(
		(state) => {
			stateService.set(storageKey, state);
		},
		[ storageKey ]
	);

	useSimpleEvent(
		stateService.onStateChanged,
		({ key }) => {
			if (key === storageKey) {
				setLocalState(stateService.get(storageKey));
			}
		},
		[ storageKey ]
	);

	return [ localState, setState ];
}
